// frontend/src/components/FilterForm/MobileFilterForm.js
import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../theme';
import { getMunicipality } from '../../utils/GetMunicipality';
import { useTranslation } from 'react-i18next';
import FiltersModal from './FiltersModal';
import LocationTimeComboFiltersModal from './LocationTimeComboFiltersModal';
import LocationTimePicker from './LocationTimePicker';
import CategoriesFilter from './CategoriesFilter';
import Button from '../../components/Button/Button';
import MoreFilterIcon from '../Icons/MoreFilterIcon';
import { getEventTypeSvgIcon, getEventTerrainSvgIcon, getEventExperienceSvgIcon } from '../../utilities';
import useFilterFormLogic from '../../hooks/useFilterFormLogic';
import { useEventCategories } from '../../hooks/useEventCategories';

const MobileFilterForm = ({ filters, setFilters, municipality, setMunicipality }) => {
  const { t: tCategories } = useTranslation('eventCategories');
  const { t: tFilter } = useTranslation('filterForm');
  const { eventTypes } = useEventCategories();

  // Claves de categorías a usar
  const categoryKeys = eventTypes.map(type => type.key);
  const minRadius = 10;
  const maxRadius = 1000;

  // Modal states para ubicación/tiempo y para el modal de filtros “otros”
  const [showLocationTimeModal, setShowLocationTimeModal] = useState(false);
  const [showModal, setShowModal] = useState(false);

  // Estado temporal para ubicación/tiempo
  const [tempLocationFilters, setTempLocationFilters] = useState({
    address: filters.location?.address || '',
    radius: filters.radius || maxRadius,
    timeFilter: filters.timeFilter || 'flexible'
  });

  // Uso del hook para el estado de los filtros “otros”
  const { tempOtherFilters, setTempOtherFilters, handleOtherFiltersChange, handleResetOtherFilters } = useFilterFormLogic(filters);

  // const {
  //   tempOtherFilters,
  //   setTempOtherFilters, // para reiniciar el estado en cancelar/cerrar
  //   handleOtherFiltersChange,
  //   handleResetOtherFilters
  // } = useFilterFormLogic(filters);

  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);

  // Función para cerrar el modal de ubicación/tiempo
  const handleCloseLocationTimeModal = () => {
    setShowLocationTimeModal(false);
  };

  // Actualizar tempLocationFilters cuando cambien los filters
  useEffect(() => {
    setTempLocationFilters({
      address: filters.location?.address || '',
      radius: filters.radius || maxRadius,
      timeFilter: filters.timeFilter || 'flexible'
    });
  }, [filters]);

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    if (place && place.geometry) {
      const selectedAddress = place.formatted_address || '';
      setTempLocationFilters(prev => ({
        ...prev,
        address: selectedAddress
      }));
    }
  };

  // Manejo de cambios en ubicación/tiempo
  const handleLocationTimeChange = (type, value) => {
    if (type === 'radius') {
      const numericValue = Number(value);
      if (isNaN(numericValue)) return;
      const radius = Math.min(Math.max(numericValue, minRadius), maxRadius);
      setTempLocationFilters(prev => ({ ...prev, radius }));
    } else if (type === 'timeFilter') {
      setTempLocationFilters(prev => ({ ...prev, timeFilter: value }));
    }
  };

  const handleApplyLocationTime = (e) => {
    e.preventDefault();
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place && place.geometry) {
        const userLocation = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
          address: tempLocationFilters.address,
        };
        const userMunicipality = getMunicipality(place.address_components || []);
        setFilters(prev => ({
          ...prev,
          location: userLocation,
          radius: tempLocationFilters.radius,
          timeFilter: tempLocationFilters.timeFilter
        }));
        setMunicipality(userMunicipality);
        localStorage.setItem('location', JSON.stringify(userLocation));
        localStorage.setItem('municipality', userMunicipality);
      } else {
        setFilters(prev => ({
          ...prev,
          radius: tempLocationFilters.radius,
          timeFilter: tempLocationFilters.timeFilter
        }));
      }
    } else {
      setFilters(prev => ({
        ...prev,
        radius: tempLocationFilters.radius,
        timeFilter: tempLocationFilters.timeFilter
      }));
    }
    setShowLocationTimeModal(false);
  };

  const handleCancelLocationTime = (e) => {
    e.preventDefault();
    setTempLocationFilters({
      address: filters.location?.address || '',
      radius: filters.radius || maxRadius,
      timeFilter: filters.timeFilter || 'flexible'
    });
    setShowLocationTimeModal(false);
  };

  // Funciones para aplicar, cancelar y cerrar el modal de filtros “otros”
  // Add this effect to sync parent filters with modal
  useEffect(() => {
    setTempOtherFilters({
      typology: [...filters.typology],
      terrain: [...filters.terrain],
      experience: [...filters.experience],
      ticketType: [...filters.ticketType],
      needsVehicle: [...filters.needsVehicle],
      vehicleTypes: [...filters.vehicleTypes]
    });
  }, [filters, setTempOtherFilters]);

  // Update apply handler
  const handleApplyOtherFilters = () => {
    setFilters(prev => ({
      ...prev,
      typology: [...tempOtherFilters.typology],
      terrain: [...tempOtherFilters.terrain],
      experience: [...tempOtherFilters.experience],
      ticketType: [...tempOtherFilters.ticketType],
      needsVehicle: [...tempOtherFilters.needsVehicle],
      vehicleTypes: [...tempOtherFilters.vehicleTypes]
    }));
    setShowModal(false);
  };

  const handleCancelOtherFilters = (e) => {
    e && e.preventDefault();
    setTempOtherFilters({
      typology: [...filters.typology],
      terrain: Array.isArray(filters.terrain) ? [...filters.terrain] : filters.terrain ? [filters.terrain] : [],
      experience: Array.isArray(filters.experience) ? [...filters.experience] : filters.experience ? [filters.experience] : [],
      ticketType: Array.isArray(filters.ticketType) ? [...filters.ticketType] : filters.ticketType ? [filters.ticketType] : [],
      needsVehicle: Array.isArray(filters.needsVehicle) ? [...filters.needsVehicle] : filters.needsVehicle ? [filters.needsVehicle] : [],
      vehicleTypes: Array.isArray(filters.vehicleTypes) ? [...filters.vehicleTypes] : filters.vehicleTypes ? [filters.vehicleTypes] : []
    });
    setShowModal(false);
  };

  const handleCloseOtherFiltersModal = () => {
    setTempOtherFilters({
      typology: [...filters.typology],
      terrain: Array.isArray(filters.terrain) ? [...filters.terrain] : filters.terrain ? [filters.terrain] : [],
      experience: Array.isArray(filters.experience) ? [...filters.experience] : filters.experience ? [filters.experience] : [],
      ticketType: Array.isArray(filters.ticketType) ? [...filters.ticketType] : filters.ticketType ? [filters.ticketType] : [],
      needsVehicle: Array.isArray(filters.needsVehicle) ? [...filters.needsVehicle] : filters.needsVehicle ? [filters.needsVehicle] : [],
      vehicleTypes: Array.isArray(filters.vehicleTypes) ? [...filters.vehicleTypes] : filters.vehicleTypes ? [filters.vehicleTypes] : []
    });
    setShowModal(false);
  };


  const handleResetFilters = () => {
    handleResetOtherFilters();
  };

  // Cambio directo desde la vista principal (para el CategoriesFilter)
  const handleDirectFilterChange = (type, value) => {
    if (type === 'typology') {
      const updatedTypology = filters.typology.includes(value)
        ? filters.typology.filter(item => item !== value)
        : [...filters.typology, value];
      setFilters(prev => ({ ...prev, typology: updatedTypology }));
    } else {
      const currentValues = Array.isArray(filters[type])
        ? [...filters[type]]
        : filters[type] ? [filters[type]] : [];
      const updatedValues = currentValues.includes(value)
        ? currentValues.filter(item => item !== value)
        : [...currentValues, value];
      setFilters(prev => ({ ...prev, [type]: updatedValues }));
    }
  };

  return (
    <FilterContainer>
      <FormContainer>
        <MainFilters>
          <LocationTimePicker
            onClick={() => setShowLocationTimeModal(true)}
            municipality={municipality}
            filters={filters}
            tFilter={tFilter}
          />
          <MoreFilters>
            <MoreFiltersButton
              $variant="outline"
              size="m"
              $iconOnly
              type="button"
              onClick={() => setShowModal(true)}
            >
              <MoreFilterIcon stroke={theme.colors.defaultStrong} height={16} width={16} />
            </MoreFiltersButton>
          </MoreFilters>
        </MainFilters>
        <SecondaryFilters>
          <CategoriesFilter
            categoryKeys={categoryKeys}
            filters={filters}
            handleDirectFilterChange={handleDirectFilterChange}
            tCategories={tCategories}
          />
        </SecondaryFilters>
      </FormContainer>

      {showLocationTimeModal && (
        <LocationTimeComboFiltersModal
          tempLocationFilters={tempLocationFilters}
          handleLocationTimeChange={handleLocationTimeChange}
          handleCancelLocationTime={handleCancelLocationTime}
          handleApplyLocationTime={handleApplyLocationTime}
          handleCloseLocationTimeModal={handleCloseLocationTimeModal}
          autocompleteRef={autocompleteRef}
          inputRef={inputRef}
          handlePlaceSelect={handlePlaceSelect}
          minRadius={minRadius}
          maxRadius={maxRadius}
          tFilter={tFilter}
          setTempLocationFilters={setTempLocationFilters}
        />
      )}
      {showModal && (
        <FiltersModal
          tempOtherFilters={tempOtherFilters}
          handleOtherFiltersChange={handleOtherFiltersChange}
          handleCancelOtherFilters={handleCancelOtherFilters}
          handleApplyOtherFilters={handleApplyOtherFilters}
          handleCloseOtherFiltersModal={handleCloseOtherFiltersModal}
          handleResetFilters={handleResetFilters}
          categoryKeys={categoryKeys}
          tFilter={tFilter}
          tCategories={tCategories}
        />
      )}
    </FilterContainer>
  );
};

export default MobileFilterForm;

///////////////////////
// ESTILOS
///////////////////////
const FilterContainer = styled.section`
  align-items: center;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  box-shadow: 0 1px 8px 0 rgb(0 0 0 / 8%);
  display: flex;
  flex-direction: column;
  z-index: 10;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  padding-top: ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const MainFilters = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: 0 ${({ theme }) => theme.sizing.sm};
  width: 100%;
`;

const SecondaryFilters = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: space-between;
`;

const MoreFilters = styled.div``;

const MoreFiltersButton = styled(Button)`
  flex-shrink: 0;
`;
